import { c as _c } from "react/compiler-runtime";
import { Skeleton, Text, ToggleGroup } from "@flat-ui";
import { Box, Stack } from "@mui-wrapper";
import { MARKUPS_SUMMARY_CHART_SIZE, MarkupsSummaryChart } from "@pages/project-details/project-overview/markups-summary/markups-summary-chart";
import { MarkupsSummaryEmpty } from "@pages/project-details/project-overview/markups-summary/markups-summary-empty";
import { MarkupsSummaryCounts } from "@pages/project-details/project-overview/markups-summary/markups-summary-counts";
import { MarkupsSummaryLegend } from "@pages/project-details/project-overview/markups-summary/markups-summary-legend";
import { getMarkupsStatusSummary, MARKUPS_SCOPE_SELECTORS, MARKUPS_SUMMARY_SCOPE_OPTIONS } from "@pages/project-details/project-overview/markups-summary/markups-summary-utils";
import { useMarkupsSummaryLink } from "@pages/project-details/project-overview/markups-summary/use-markups-summary-link";
import { areMarkupAssigneesUsedSelector, areMarkupStatusesUsedSelector } from "@store/markups/markups-selector";
import { isFetchingMarkupsIElementsSelector, markupsIElementsFetchingStatusSelector } from "@store/sdb-i-elements/sdb-i-elements-selectors";
import { useAppSelector } from "@store/store-helper";
import { AnnotationsSummaryEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Texts that depend on the selected scope */
const SCOPE_TEXTS = {
  mine: {
    centerLabel: "Assigned to me",
    emptyMessage: "There are no annotations assigned to you in this project."
  },
  created: {
    centerLabel: "Created by me",
    emptyMessage: "There are no annotations created by you in this project."
  },
  all: {
    centerLabel: "Total",
    emptyMessage: "There are no annotations in this project."
  }
};
/** Summary of a single scope: an empty message or the chart with its legend */
function MarkupsSummaryView(t0) {
  const $ = _c(18);
  const {
    scope
  } = t0;
  const markups = useAppSelector(MARKUPS_SCOPE_SELECTORS[scope]);
  const {
    centerLabel,
    emptyMessage
  } = SCOPE_TEXTS[scope];
  const getLink = useMarkupsSummaryLink(scope);
  let summary;
  let t1;
  if ($[0] !== markups) {
    summary = getMarkupsStatusSummary(markups);
    t1 = summary.reduce(_temp, 0);
    $[0] = markups;
    $[1] = summary;
    $[2] = t1;
  } else {
    summary = $[1];
    t1 = $[2];
  }
  const total = t1;
  if (!total) {
    let t2;
    if ($[3] !== emptyMessage) {
      t2 = <MarkupsSummaryEmpty message={emptyMessage} />;
      $[3] = emptyMessage;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      xs: "column",
      sm: "row"
    };
    t3 = {
      alignItems: "center",
      gap: 4
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== centerLabel || $[8] !== getLink || $[9] !== summary || $[10] !== total) {
    t4 = <MarkupsSummaryChart summary={summary} total={total} centerLabel={centerLabel} getLink={getLink} />;
    $[7] = centerLabel;
    $[8] = getLink;
    $[9] = summary;
    $[10] = total;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== getLink || $[13] !== summary) {
    t5 = <MarkupsSummaryLegend summary={summary} getLink={getLink} />;
    $[12] = getLink;
    $[13] = summary;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <Stack direction={t2} sx={t3}>{t4}{t5}</Stack>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}

/**
 * Content of the summary: a skeleton, the plain amounts or the view of the selected scope.
 * All views share one grid cell, so switching scopes keeps the height of the tallest one.
 */
function _temp(sum, t0) {
  const {
    count
  } = t0;
  return sum + count;
}
function MarkupsSummaryContent(t0) {
  const $ = _c(8);
  const {
    isLoading,
    scope,
    areStatusesUsed,
    hasFailed
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton variant="rectangular" width="100%" height={MARKUPS_SUMMARY_CHART_SIZE} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (hasFailed) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MarkupsSummaryEmpty isError={true} message="Failed to fetch the project annotations. Please reload the page and try again." />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!areStatusesUsed) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MarkupsSummaryCounts />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "grid"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== scope) {
    t2 = MARKUPS_SUMMARY_SCOPE_OPTIONS.map(t3 => {
      const {
        value
      } = t3;
      return <Box key={value} inert={value !== scope} sx={{
        gridArea: "1 / 1",
        alignSelf: "center",
        opacity: value === scope ? 1 : 0
      }}><MarkupsSummaryView scope={value} /></Box>;
    });
    $[4] = scope;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <Box sx={t1}>{t2}</Box>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/**
 * Summary of the project annotations grouped by status, of the ones assigned to or created by the current user or of everyone.
 * Until the project uses statuses, only the amounts are shown, and until anyone is assigned, the assigned scope is not offered.
 */
export function MarkupsSummary(t0) {
  const $ = _c(25);
  const {
    isLoading
  } = t0;
  const [selectedScope, setScope] = useState("mine");
  const {
    trackEvent
  } = useTrackEvent();
  const isFetchingMarkups = useAppSelector(isFetchingMarkupsIElementsSelector);
  const hasFailed = useAppSelector(markupsIElementsFetchingStatusSelector) === "rejected";
  const areStatusesUsed = useAppSelector(areMarkupStatusesUsedSelector);
  const areAssigneesUsed = useAppSelector(areMarkupAssigneesUsedSelector);
  const isSummaryLoading = isLoading || isFetchingMarkups;
  const scope = !areAssigneesUsed && selectedScope === "mine" ? "all" : selectedScope;
  let t1;
  if ($[0] !== areAssigneesUsed) {
    t1 = areAssigneesUsed ? MARKUPS_SUMMARY_SCOPE_OPTIONS : MARKUPS_SUMMARY_SCOPE_OPTIONS.filter(_temp2);
    $[0] = areAssigneesUsed;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scopeOptions = t1;
  let t2;
  if ($[2] !== scope || $[3] !== trackEvent) {
    t2 = function onScopeChange(newScope) {
      if (newScope === scope) {
        return;
      }
      setScope(newScope);
      trackEvent({
        name: AnnotationsSummaryEvents.changeScope,
        props: {
          scope: newScope,
          place: "overview"
        }
      });
    };
    $[2] = scope;
    $[3] = trackEvent;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onScopeChange = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      gap: 2
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1
    };
    t5 = <Text variant="heading16">Annotations</Text>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== areStatusesUsed || $[9] !== hasFailed || $[10] !== isSummaryLoading || $[11] !== onScopeChange || $[12] !== scope || $[13] !== scopeOptions) {
    t6 = !isSummaryLoading && !hasFailed && areStatusesUsed && <ToggleGroup items={scopeOptions} value={scope} onChange={onScopeChange} />;
    $[8] = areStatusesUsed;
    $[9] = hasFailed;
    $[10] = isSummaryLoading;
    $[11] = onScopeChange;
    $[12] = scope;
    $[13] = scopeOptions;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <Stack direction="row" sx={t4}>{t5}{t6}</Stack>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== areStatusesUsed || $[18] !== hasFailed || $[19] !== isSummaryLoading || $[20] !== scope) {
    t8 = <MarkupsSummaryContent isLoading={isSummaryLoading} scope={scope} areStatusesUsed={areStatusesUsed} hasFailed={hasFailed} />;
    $[17] = areStatusesUsed;
    $[18] = hasFailed;
    $[19] = isSummaryLoading;
    $[20] = scope;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <Stack sx={t3}>{t7}{t8}</Stack>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp2(t0) {
  const {
    value
  } = t0;
  return value !== "mine";
}